import type { ReactNode } from "react";
import { Text, View } from "react-native";

import { GemMark } from "@/components/brand/GemMark";
import { cn } from "@/lib/ui/cn";

/**
 * The shared empty and first-run state.
 *
 * Optionally leads with the Trovie gem mark (`mark`), then the title, subtitle and an optional
 * `action`: a CTA (for example "Add your first item") under the copy, so a first-run screen offers
 * the next step directly instead of describing a floating button in prose.
 *
 * `mark` is only on or off because the flat mark has no expressions to choose from (ADR-0046).
 */
export function EmptyState(
  { title, subtitle, action, mark, headingLevel = 2, className, testID }: {
    title: string;
    subtitle?: string;
    /** The CTA shown under the copy. */
    action?: ReactNode;
    /** Leads with the Trovie gem mark above the title. */
    mark?: boolean;
    /**
     * The title's depth in the document outline. react-native-web renders a `header` role as
     * `<h{level}>`, and as `<h1>` when no level is given.
     *
     * Defaults to 2 because an empty state usually sits inside a screen that already owns the
     * page's `<h1>`, such as a no-results list on the storefront or an empty tab. Pass `1` only
     * where the empty state is the page, as on the browse host's landing.
     */
    headingLevel?: 1 | 2;
    /** Extra spacing around the default layout, such as more room above it under a filter row. */
    className?: string;
    testID?: string;
  },
) {
  return (
    <View
      testID={testID}
      className={cn("workspace-empty flex-1 items-center justify-center px-8", className)}
    >
      {mark
        ? (
          <View className="mb-4">
            <GemMark size={72} />
          </View>
        )
        : null}
      <Text
        accessibilityRole="header"
        aria-level={headingLevel}
        className="text-lg font-title text-foreground"
      >
        {title}
      </Text>
      {subtitle
        ? <Text className="mt-1 text-center font-body text-muted-foreground">{subtitle}</Text>
        : null}
      {action ? <View className="mt-5">{action}</View> : null}
    </View>
  );
}
